import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAvailability, getCurrentAvailability, setAvailability, deleteAvailability, getTeamAvailability } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { CalendarClock, Plus, Trash2, Check, X, Users } from 'lucide-react';
import { format, addDays } from 'date-fns';

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

export const AvailabilityPage = () => {
  const { hasRole } = useAuth();
  const [currentAvailability, setCurrentAvailability] = useState(null);
  const [teamAvailability, setTeamAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const [formData, setFormData] = useState({
    effective_from: format(new Date(), 'yyyy-MM-dd'),
    effective_until: '',
    notes: '',
    slots: DAYS_OF_WEEK.map(day => ({
      day: day.key,
      start_time: '09:00',
      end_time: '17:00',
      is_available: day.key !== 'saturday' && day.key !== 'sunday'
    }))
  });

  const fetchData = useCallback(async () => {
    try {
      const currentRes = await getCurrentAvailability();
      setCurrentAvailability(currentRes.data);

      if (hasRole(['admin', 'manager', 'supervisor'])) {
        const teamRes = await getTeamAvailability(format(selectedDate, 'yyyy-MM-dd'));
        setTeamAvailability(teamRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, hasRole]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSlotChange = (dayKey, field, value) => {
    setFormData(prev => ({
      ...prev,
      slots: prev.slots.map(slot =>
        slot.day === dayKey ? { ...slot, [field]: value } : slot
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await setAvailability({
        slots: formData.slots,
        effective_from: formData.effective_from,
        effective_until: formData.effective_until || null,
        notes: formData.notes || null
      });
      toast.success('Availability saved');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save availability');
    }
  };

  const handleDelete = async () => {
    if (!currentAvailability?.id) return;
    if (!window.confirm('Delete your current availability settings?')) return;
    try {
      await deleteAvailability(currentAvailability.id);
      toast.success('Availability deleted');
      setCurrentAvailability(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleDateSelect = async (date) => {
    setSelectedDate(date);
    if (hasRole(['admin', 'manager', 'supervisor'])) {
      try {
        const teamRes = await getTeamAvailability(format(date, 'yyyy-MM-dd'));
        setTeamAvailability(teamRes.data);
      } catch (error) {
        console.error('Failed to fetch team availability:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="availability-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="availability-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">
            Availability
          </h1>
          <p className="text-muted-foreground mt-1">
            Set your working availability for scheduling
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="set-availability-btn">
              <Plus className="w-4 h-4 mr-2" />
              Set Availability
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Set Your Availability</DialogTitle>
              <DialogDescription>
                Define when you're available to work each week
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Effective From</Label>
                  <Input
                    type="date"
                    value={formData.effective_from}
                    onChange={(e) => setFormData(prev => ({ ...prev, effective_from: e.target.value }))}
                    data-testid="availability-from"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Until (Optional)</Label>
                  <Input
                    type="date"
                    value={formData.effective_until}
                    onChange={(e) => setFormData(prev => ({ ...prev, effective_until: e.target.value }))}
                    data-testid="availability-until"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <Label>Weekly Schedule</Label>
                <div className="space-y-3">
                  {DAYS_OF_WEEK.map((day) => {
                    const slot = formData.slots.find(s => s.day === day.key);
                    return (
                      <div
                        key={day.key}
                        className={`flex items-center gap-4 p-3 rounded-xl ${slot?.is_available ? 'bg-emerald-50' : 'bg-muted/50'}`}
                      >
                        <div className="w-24">
                          <span className="font-medium">{day.label}</span>
                        </div>
                        <Switch
                          checked={slot?.is_available}
                          onCheckedChange={(checked) => handleSlotChange(day.key, 'is_available', checked)}
                          data-testid={`availability-${day.key}-toggle`}
                        />
                        {slot?.is_available && (
                          <>
                            <Input
                              type="time"
                              value={slot?.start_time}
                              onChange={(e) => handleSlotChange(day.key, 'start_time', e.target.value)}
                              className="w-28"
                              data-testid={`availability-${day.key}-start`}
                            />
                            <span className="text-muted-foreground">to</span>
                            <Input
                              type="time"
                              value={slot?.end_time}
                              onChange={(e) => handleSlotChange(day.key, 'end_time', e.target.value)}
                              className="w-28"
                              data-testid={`availability-${day.key}-end`}
                            />
                          </>
                        )}
                        {!slot?.is_available && (
                          <span className="text-sm text-muted-foreground">Not available</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  placeholder="Any additional notes about your availability..."
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  data-testid="availability-notes"
                />
              </div>

              <Button type="submit" className="w-full" data-testid="save-availability-btn">
                Save Availability
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Current Availability */}
      <Card data-testid="current-availability-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 font-['Manrope']">
                <CalendarClock className="w-5 h-5" />
                Your Current Availability
              </CardTitle>
              <CardDescription>
                {currentAvailability
                  ? `Effective from ${currentAvailability.effective_from}`
                  : 'No availability set'}
              </CardDescription>
            </div>
            {currentAvailability && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDelete}
                className="text-destructive"
                data-testid="delete-availability-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {currentAvailability ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {DAYS_OF_WEEK.map((day) => {
                const slot = currentAvailability.slots?.find(s => s.day === day.key);
                const isAvailable = slot?.is_available;
                return (
                  <div
                    key={day.key}
                    className={`p-3 rounded-xl text-center ${isAvailable ? 'bg-emerald-50' : 'bg-muted/50'}`}
                  >
                    <p className="text-sm font-medium">{day.label.slice(0, 3)}</p>
                    {isAvailable ? (
                      <>
                        <Check className="w-4 h-4 mx-auto my-1 text-emerald-600" />
                        <p className="text-xs text-muted-foreground font-mono">
                          {slot?.start_time}-{slot?.end_time}
                        </p>
                      </>
                    ) : (
                      <X className="w-4 h-4 mx-auto my-1 text-muted-foreground" />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state py-8">
              <CalendarClock className="empty-state-icon" />
              <p className="text-muted-foreground">No availability set</p>
              <p className="text-sm text-muted-foreground mt-1">
                Set your weekly availability for better scheduling
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Team Availability - Managers only */}
      {hasRole(['admin', 'manager', 'supervisor']) && (
        <Card data-testid="team-availability-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <Users className="w-5 h-5" />
              Team Availability
            </CardTitle>
            <CardDescription>
              View team availability for a specific date
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <Label className="mb-2 block">Select Date</Label>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  className="rounded-md border"
                />
              </div>
              <div>
                <Label className="mb-2 block">
                  Available on {format(selectedDate, 'EEEE, MMM d')}
                </Label>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {teamAvailability.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4">No availability data</p>
                  ) : (
                    teamAvailability.map((member) => (
                      <div
                        key={member.user_id}
                        className={`flex items-center justify-between p-3 rounded-lg ${member.is_available ? 'bg-emerald-50' : 'bg-muted/50'}`}
                        data-testid={`team-member-${member.user_id}`}
                      >
                        <span className="font-medium">{member.user_name}</span>
                        {member.is_available ? (
                          <div className="flex items-center gap-2">
                            {member.slots?.map((slot, idx) => (
                              <Badge key={idx} className="badge-success text-xs">
                                {slot.start_time}-{slot.end_time}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <Badge variant="outline">Not Available</Badge>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
